import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { stats } from '@fiverr-private/obs';
import { useShouldOpenUserActivationModal } from '@fiverr-private/code_activation_triggers';
import { BriefCreationDrawerComponent } from '../../components';
import {
  BriefCreationContextProviderProps,
  Rollouts,
  useBriefCreationContext,
} from '../../context/BriefCreationContext';
import { BriefCreationContextProvider } from '../../context';
import { METRICS_PREFIX } from '../../constants';
import { logger } from '../../utils/logger';

export interface BriefCreationDrawerWrapperProps
  extends Rollouts,
    Pick<
      BriefCreationContextProviderProps,
      'currency' | 'isOpen' | 'source' | 'initialPrompt' | 'onOpen' | 'enableEnhancedUx' | 'enableScreeningQuestions'
    > {
  setIsOpen: (isOpen: boolean) => void;
  onSuccess?: () => void;
}

const Drawer = ({ isOpen = false, isUserNotActivated }: { isOpen?: boolean; isUserNotActivated?: boolean }) => {
  const { setIsOpen, openUserActivationModal } = useBriefCreationContext();

  useEffect(() => {
    // will wait until the user activation info is present
    if (isUserNotActivated !== undefined) {
      if (isUserNotActivated && isOpen) {
        setIsOpen(false);
        openUserActivationModal({
          onSuccess: () => setIsOpen(true),
        });
      } else if (!isUserNotActivated) {
        setIsOpen(isOpen);
      }
    }
  }, [isOpen, setIsOpen, isUserNotActivated, openUserActivationModal]);

  return <BriefCreationDrawerComponent />;
};

const onError = (error) => {
  logger.error(error, {
    description: 'Failed to lazy load BriefCreationDrawerWrapper',
  });
  stats.count(`${METRICS_PREFIX}.lazy_load_brief_creation_drawer_wrapper`, 'error');
};

const BriefCreationDrawerWrapper = ({
  currency,
  isOpen,
  setIsOpen,
  source,
  onSuccess,
  rollouts,
  initialPrompt,
  onOpen,
  enableEnhancedUx,
  enableScreeningQuestions,
}: BriefCreationDrawerWrapperProps) => {
  const shouldOpenUserActivationModal = useShouldOpenUserActivationModal();
  const [isUserNotActivated, setIsUserNotActivated] = useState<boolean | undefined>();

  useEffect(() => {
    const checkUserActivation = async () => {
      const notActivated = await shouldOpenUserActivationModal();
      if (notActivated !== undefined) {
        setIsUserNotActivated(notActivated);
      }
    };

    checkUserActivation();
  }, [shouldOpenUserActivationModal]);

  return (
    <ErrorBoundary onError={onError}>
      <BriefCreationContextProvider
        rollouts={rollouts}
        currency={currency}
        setIsOpen={setIsOpen}
        source={source}
        onSuccess={onSuccess}
        initialPrompt={initialPrompt}
        onOpen={onOpen}
        enableEnhancedUx={enableEnhancedUx}
        enableScreeningQuestions={enableScreeningQuestions}
      >
        <Drawer isOpen={isOpen} isUserNotActivated={isUserNotActivated} />
      </BriefCreationContextProvider>
    </ErrorBoundary>
  );
};

export default BriefCreationDrawerWrapper;
